/**
 * Peak Premium Web Components & Dynamic Interactions (July 2026)
 * Native Vanilla JS - Zero Framework Overhead - High Performance
 */

/* 1. Dynamic Spec Sheet & Resource Tabs Component */
class PeakSpecsTabs extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.contentPanels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

    if (!this.tabs.length || !this.contentPanels.length) return;

    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);

    const selectedTab = this.tabs.find(tab => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
    this.activateTab(selectedTab);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
  }

  handleClick(event) {
    const tab = event.target.closest('[role="tab"]');
    if (!tab || !this.contains(tab)) return;

    event.preventDefault();
    this.activateTab(tab);
  }

  handleKeydown(event) {
    const currentTab = event.target.closest('[role="tab"]');
    if (!currentTab || !this.contains(currentTab)) return;

    const currentIndex = this.tabs.indexOf(currentTab);
    let targetIndex;

    switch (event.key) {
      case 'ArrowLeft':
        targetIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'ArrowRight':
        targetIndex = (currentIndex + 1) % this.tabs.length;
        break;
      case 'Home':
        targetIndex = 0;
        break;
      case 'End':
        targetIndex = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const targetTab = this.tabs[targetIndex];
    this.activateTab(targetTab);
    targetTab.focus();
  }

  activateTab(activeTab) {
    const activePanelId = activeTab.getAttribute('aria-controls');

    this.tabs.forEach(tab => {
      const isActive = tab === activeTab;
      tab.classList.toggle('active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
      tab.style.color = isActive ? '#FFFFFF' : 'var(--peak-text-secondary)';
      tab.style.borderBottomColor = isActive ? 'var(--peak-purple)' : 'transparent';
    });

    this.contentPanels.forEach(panel => {
      const isActive = panel.id === activePanelId;
      panel.classList.toggle('active', isActive);
      panel.hidden = !isActive;
      panel.style.display = isActive ? 'block' : 'none';
    });
  }
}

if (!customElements.get('peak-specs-tabs')) {
  customElements.define('peak-specs-tabs', PeakSpecsTabs);
}


/* 2. Interactive Gallery Viewer Custom Element */
class PeakGalleryViewer extends HTMLElement {
  constructor() {
    super();
    this.initializeDesktopLayout = this.initializeDesktopLayout.bind(this);
  }

  connectedCallback() {
    const thumbnails = this.querySelectorAll('.thumbnail-list__item button');
    this.slides = this.querySelectorAll('.product__media-item');

    if (!this.slides.length) return;

    // Click/touch switching for thumbnail slider transitions
    thumbnails.forEach(thumbnail => {
      thumbnail.addEventListener('click', (e) => {
        e.preventDefault();
        const li = thumbnail.closest('li');
        if (!li) return;
        const targetMediaId = li.getAttribute('data-target');

        // Update active slide state
        this.slides.forEach(slide => {
          const mediaId = slide.getAttribute('data-media-id');
          // Match direct ID or containing target
          const isMatch = mediaId === targetMediaId || (mediaId && mediaId.includes(targetMediaId));
          
          if (isMatch) {
            slide.classList.add('is-active');
            slide.style.display = 'block';
            slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          } else {
            slide.classList.remove('is-active');
            // If slider isn't mobile-active, hide non-active slides to allow clean stacked overlays on desktop
            if (window.innerWidth >= 750) {
              slide.style.display = 'none';
            }
          }
        });

        // Set aria attributes for accessibility
        thumbnails.forEach(btn => {
          btn.setAttribute('aria-current', btn === thumbnail ? 'true' : 'false');
        });
      });
    });

    window.addEventListener('resize', this.initializeDesktopLayout, { passive: true });
    this.initializeDesktopLayout();

    // Smooth hover zoom coordinate scaling
    this.slides.forEach(slide => {
      const mediaContainer = slide.querySelector('.media');
      const img = slide.querySelector('img');
      
      if (mediaContainer && img) {
        mediaContainer.addEventListener('mousemove', (e) => {
          const rect = mediaContainer.getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;
          img.style.transformOrigin = `${x}% ${y}%`;
          img.style.transform = 'scale(1.5)';
          img.style.transition = 'transform 0.1s ease';
        });

        mediaContainer.addEventListener('mouseleave', () => {
          img.style.transform = 'scale(1)';
          img.style.transformOrigin = 'center center';
          img.style.transition = 'transform 0.35s cubic-bezier(0.16, 1, 0.3, 1)';
        });
      }
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.initializeDesktopLayout);
  }

  initializeDesktopLayout() {
    if (!this.slides) return;
    if (window.innerWidth >= 750) {
      this.slides.forEach((slide, idx) => {
        if (idx === 0 || slide.classList.contains('is-active')) {
          slide.classList.add('is-active');
          slide.style.display = 'block';
        } else {
          slide.style.display = 'none';
        }
      });
    } else {
      this.slides.forEach(slide => {
        slide.style.display = '';
      });
    }
  }
}

if (!customElements.get('peak-gallery-viewer')) {
  customElements.define('peak-gallery-viewer', PeakGalleryViewer);
}


/* 3. Sticky Bottom Add-to-Cart scroll trigger */
function initStickyATC() {
  const nativeSubmitBtn = document.querySelector('.product-form__submit');
  const stickyBar = document.getElementById('StickyATCBar');
  const stickyBtn = document.getElementById('StickyATCBttn');

  if (nativeSubmitBtn && stickyBar) {
    // If an observer was active from a previous load, clean it up
    if (window.stickyATCObserver) {
      window.stickyATCObserver.disconnect();
    }

    window.stickyATCObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        // Display bar only when scrolling past the main buy button, on mobile viewport
        if (!entry.isIntersecting && window.innerWidth < 750) {
          stickyBar.classList.add('is-visible');
        } else {
          stickyBar.classList.remove('is-visible');
        }
      });
    }, {
      root: null,
      threshold: 0,
      rootMargin: '0px 0px -100px 0px'
    });

    window.stickyATCObserver.observe(nativeSubmitBtn);

    // Sync button press with native checkout form submission
    if (stickyBtn) {
      stickyBtn.replaceWith(stickyBtn.cloneNode(true));
      const newStickyBtn = document.getElementById('StickyATCBttn');
      newStickyBtn.addEventListener('click', (e) => {
        e.preventDefault();
        nativeSubmitBtn.click();
      });
    }

    // Clean up any stale resize listener on window from previous runs
    if (window.stickyATCResizeHandler) {
      window.removeEventListener('resize', window.stickyATCResizeHandler);
    }

    // Store the exact reference to remove it cleanly next time
    window.stickyATCResizeHandler = () => {
      if (window.innerWidth >= 750) {
        stickyBar.classList.remove('is-visible');
      }
    };

    // Hide sticky bar when viewport is resized to desktop width
    window.addEventListener('resize', window.stickyATCResizeHandler, { passive: true });
  }
}

document.addEventListener('DOMContentLoaded', initStickyATC);

// Listen for Theme Editor section loads to re-trigger binding
document.addEventListener('shopify:section:load', (event) => {
  if (event.detail && event.detail.sectionId && event.detail.sectionId.includes('product')) {
    initStickyATC();
  } else {
    initStickyATC();
  }
});
